/**
 * Per-gender Section Analytics — fire-and-forget event tracking.
 *
 * Two hooks:
 *
 *   useImpressionTracker(sectionId)
 *     — returns a ref. Attach to the section's root element. Fires exactly one
 *       `impression` event when the section enters the viewport. De-dupes by
 *       (sectionId, gender, session) so refreshing the page doesn't double-count.
 *
 *   trackClick(sectionId, target_url, target_id?)
 *     — manually fire a `click` event from any CTA/link handler.
 *
 * Session ID is generated once per browser session and kept in sessionStorage
 * so impressions/clicks within the same visit can be grouped.
 */
import { useEffect, useRef } from "react";
import { useGender } from "@/context/GenderContext";

const SESSION_KEY = "xyz_analytics_session";
const SEEN_KEY = "xyz_analytics_seen";  // per-session impressions guard

function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (_e) {
    return `s_anon_${Date.now()}`;
  }
}

function seenSet() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]"));
  } catch (_e) {
    return new Set();
  }
}

function markSeen(key) {
  try {
    const s = seenSet();
    s.add(key);
    sessionStorage.setItem(SEEN_KEY, JSON.stringify([...s]));
  } catch (_e) {
    // sessionStorage disabled — accept double counting silently
  }
}

async function postEvent(body) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`;
  try {
    // sendBeacon is the right tool here — fire-and-forget, doesn't block
    // unload/navigation, and the browser handles delivery. Falls back to
    // fetch() with keepalive for browsers that don't support it.
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (_e) {
    // analytics must never break the UI
  }
}

export function useImpressionTracker(sectionId) {
  const { gender } = useGender();
  const ref = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!sectionId || !ref.current || firedRef.current) return;
    const el = ref.current;
    const sessionId = getSessionId();
    const dedupeKey = `${sectionId}|${gender}|${sessionId}`;
    if (seenSet().has(dedupeKey)) {
      firedRef.current = true;
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            markSeen(dedupeKey);
            postEvent({
              event_type: "impression",
              section_id: sectionId,
              gender,
              session_id: sessionId,
            });
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.4 } // ~40% of section visible counts as an impression
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sectionId, gender]);

  return ref;
}

export function trackClick(sectionId, target_url, target_id) {
  const sessionId = getSessionId();
  // We can't useGender() here (not a hook) — read from localStorage directly
  // since GenderContext mirrors there.
  let gender = "men";
  try {
    const stored = localStorage.getItem("xyz_gender_v1");
    if (stored) gender = stored;
  } catch (_e) {}
  postEvent({
    event_type: "click",
    section_id: sectionId,
    gender,
    target_url: target_url || "",
    target_id: target_id || "",
    session_id: sessionId,
  });
}
